import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface TidalDragInteractiveProps {
  width?: number;
  height?: number;
}

export const TidalDragInteractive: React.FC<TidalDragInteractiveProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const [tideType, setTideType] = useState<'Spring Tide' | 'Neap Tide' | 'Normal Tide'>('Spring Tide');

  const sunX = 40;
  const earthX = size / 2;
  const earthY = size / 2;
  const orbitRadius = 90;
  
  const moonRef = useRef({ 
    angle: 0,
    x: earthX + orbitRadius,
    y: earthY
  });
  
  const isDragging = useRef(false);
  const canvasElementRef = useRef<HTMLCanvasElement | null>(null);

  const setCanvasRef = (el: HTMLCanvasElement | null) => {
    canvasElementRef.current = el;
  };

  useEffect(() => {
    const handleDown = (e: MouseEvent | TouchEvent) => {
      if (!canvasElementRef.current) return;
      const rect = canvasElementRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      
      const dx = x - moonRef.current.x;
      const dy = y - moonRef.current.y;
      if (Math.sqrt(dx * dx + dy * dy) < 30) {
        isDragging.current = true;
      }
    };

    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging.current || !canvasElementRef.current) return;
      e.preventDefault();
      const rect = canvasElementRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const x = clientX - rect.left;
      const y = clientY - rect.top;

      const angle = Math.atan2(y - earthY, x - earthX);
      moonRef.current.angle = angle;
      moonRef.current.x = earthX + Math.cos(angle) * orbitRadius;
      moonRef.current.y = earthY + Math.sin(angle) * orbitRadius;
    };

    const handleUp = () => {
      isDragging.current = false;
    };

    const canvas = canvasElementRef.current;
    if (canvas) {
      canvas.addEventListener('mousedown', handleDown);
      window.addEventListener('mousemove', handleMove, { passive: false });
      window.addEventListener('mouseup', handleUp);
      
      canvas.addEventListener('touchstart', handleDown);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleUp);
    }

    return () => {
      if (canvas) {
        canvas.removeEventListener('mousedown', handleDown);
        canvas.removeEventListener('touchstart', handleDown);
      }
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [earthX, earthY, orbitRadius]);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number, canvas: HTMLCanvasElement) => {
    if (canvasElementRef.current !== canvas) {
      setCanvasRef(canvas);
    }

    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    // Draw Sun
    ctx.save();
    const sunGrad = ctx.createRadialGradient(sunX, earthY, 10, sunX, earthY, 40);
    sunGrad.addColorStop(0, '#FFFFFF');
    sunGrad.addColorStop(0.3, '#FDE047');
    sunGrad.addColorStop(1, 'rgba(253, 224, 71, 0)');
    ctx.fillStyle = sunGrad;
    ctx.beginPath();
    ctx.arc(sunX, earthY, 40, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Draw Moon Orbit
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.lineWidth = 1;
    ctx.arc(earthX, earthY, orbitRadius, 0, Math.PI * 2);
    ctx.stroke();

    const mAngle = moonRef.current.angle;
    // Normalize angle between 0 and 2PI
    let deg = (mAngle * 180) / Math.PI;
    if (deg < 0) deg += 360;

    let type: 'Spring Tide' | 'Neap Tide' | 'Normal Tide' = 'Normal Tide';
    let stretch = 1.2; // Normal
    let bulgeAngle = mAngle;

    // Sun is at 180 degrees relative to Earth (since it's on the left, Earth is center)
    // Wait, Sun is at x=40, Earth at size/2. Angle from Earth to Sun is PI (180 deg)
    
    // Spring tide: Moon at ~0/360 or ~180 (New Moon or Full Moon)
    if (deg > 340 || deg < 20 || (deg > 160 && deg < 200)) {
      type = 'Spring Tide';
      stretch = 1.45; // Max bulge
      // Align bulge with Moon
      bulgeAngle = mAngle;
    } 
    // Neap tide: Moon at ~90 or ~270 (Quarter moons)
    else if ((deg > 70 && deg < 110) || (deg > 250 && deg < 290)) {
      type = 'Neap Tide';
      stretch = 1.05; // Min bulge
      // Bulge still roughly points towards Moon but is very weak
      bulgeAngle = mAngle;
    } else {
      stretch = 1.25;
      bulgeAngle = mAngle;
    }

    setTideType(type);

    // Draw Earth
    const eRadius = 25;
    
    // Draw Tidal Bulge
    ctx.save();
    ctx.translate(earthX, earthY);
    ctx.rotate(bulgeAngle);
    ctx.beginPath();
    // Use an ellipse to show the bulge pointing towards the moon (and opposite)
    ctx.ellipse(0, 0, eRadius * stretch, eRadius * (2 - stretch * 0.8), 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(56, 189, 248, 0.4)'; // Water blue
    ctx.fill();
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.8)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();

    // Solid Earth Core
    ctx.fillStyle = '#4A90D9';
    ctx.beginPath();
    ctx.arc(earthX, earthY, eRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#22C55E';
    ctx.beginPath();
    ctx.arc(earthX - 5, earthY - 5, 8, 0, Math.PI * 2);
    ctx.arc(earthX + 8, earthY + 6, 10, 0, Math.PI * 2);
    ctx.fill();

    // Draw Moon
    ctx.fillStyle = '#A0A0A0';
    ctx.beginPath();
    ctx.arc(moonRef.current.x, moonRef.current.y, 10, 0, Math.PI * 2);
    ctx.fill();

    // Interaction Hint
    if (!isDragging.current) {
      ctx.fillStyle = `rgba(255, 255, 255, ${Math.abs(Math.sin(frameCount * 0.05)) * 0.5 + 0.2})`;
      ctx.textAlign = 'center';
      ctx.font = '12px sans-serif';
      ctx.fillText('Drag the Moon', moonRef.current.x, moonRef.current.y - 20);
    }

  }, [size]);

  const canvasHookRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size - 60, position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #1E293B', cursor: isDragging.current ? 'grabbing' : 'grab' }}>
        <canvas ref={canvasHookRef} />
      </div>
      
      <div style={{ 
        marginTop: '16px', 
        width: '100%', 
        textAlign: 'center',
        height: '30px',
      }}>
        <div style={{ fontSize: '18px', fontWeight: 'bold', color: tideType === 'Spring Tide' ? '#4ADE80' : tideType === 'Neap Tide' ? '#F87171' : '#94A3B8' }}>
          {tideType === 'Spring Tide' && 'SPRING TIDE — large waves'}
          {tideType === 'Neap Tide' && 'NEAP TIDE — small waves'}
          {tideType === 'Normal Tide' && 'Normal Tide'}
        </div>
      </div>
    </div>
  );
};
